import { FC, useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { useAuth } from '@frontegg/react'
import { UserServiceV4 } from 'services'
import theme from 'theme'

/* Utils */
import { hasAnyRole, hasPermission, Permission, Role } from '../../../../../lib/utils'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { closeToast, openToast, ToastVariant } from '../../../../../store/toasts/toastSlice'
import { modalPropsSelector, ModalType, openModal } from '../../../../../store/modals/modalSlice'
import { User } from '../../../../../models'

/* Components */
import { disabledStyles, ThemeButton } from '../../../buttons'
import { Actions, Content } from './userProfile.styles'
import { CopyIcon, EmailIcon } from '../../../svg'
import { PermissionTooltip } from '../../../tooltip'

interface Props {
  user?: User
}

const UserProfile: FC<Props> = ({ user: thisUser }) => {
  const dispatch = useAppDispatch()
  const modalProps = useAppSelector(modalPropsSelector)
  const user = thisUser || modalProps?.props?.user
  const { user: authUser } = useAuth()

  const handleChangeName = () => {
    if (!hasPermission(Permission.UsersSelfProfileUpdate, authUser)) {
      return
    }
    dispatch(openModal({ type: ModalType.changeName, props: modalProps.props }))
  }

  const handleChangeEmail = () => {
    if (!hasPermission(Permission.UsersSelfProfileUpdate, authUser)) {
      return
    }
    dispatch(openModal({ type: ModalType.changeEmail, props: modalProps.props }))
  }

  const handleChangeRole = () => {
    if (!user?.role || !hasPermission(Permission.UsersSelfProfileUpdate, authUser)) {
      return
    }
    dispatch(openModal({ type: ModalType.changeRole, props: modalProps.props }))
  }

  const handleResendInvitationLink = async () => {
    if (!user) return

    try {
      await UserServiceV4.reinviteUser(user.id)

      dispatch(
        openToast({
          variant: ToastVariant.success,
          props: {
            text: 'Reset Invitation Link Sent',
            description: 'Check your email for a link.'
          }
        })
      )
    } catch (e) {
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed sending link',
            description:
              (e as any).response?.data.message ?? 'There was a problem while sending your password reset link.'
          }
        })
      )
    } finally {
      setTimeout(() => {
        dispatch(closeToast())
      }, 5000)
    }
  }

  const handleEmailPasswordResetLink = async () => {
    if (!hasPermission(Permission.UsersSelfPasswordUpdate, authUser)) {
      return
    }
    if (!user) return
    try {
      if (user.id === authUser?.id) {
        await UserServiceV4.resetPasswordSelf()

        dispatch(
          openToast({
            variant: ToastVariant.success,
            props: {
              text: 'Reset Password Link Sent',
              description: 'Check your email for a link.'
            }
          })
        )
      } else {
        await UserServiceV4.resetPasswordForUser(user.id)

        dispatch(
          openToast({
            variant: ToastVariant.success,
            props: {
              text: 'Reset Password Link Sent',
              description: `Email sent to ${user.email}`
            }
          })
        )
      }
    } catch (e) {
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed sending link',
            description:
              (e as any).response?.data.message ?? 'There was a problem while sending your password reset link.'
          }
        })
      )
    } finally {
      setTimeout(() => {
        dispatch(closeToast())
      }, 5000)
    }
  }

  const handleCopyPasswordResetLink = async () => {
    if (!hasPermission(Permission.UsersSelfProfileUpdate, authUser)) {
      return
    }
    if (!user) return

    try {
      const { link } = await UserServiceV4.getPasswordResetLink(user.id)
      window.navigator.clipboard.writeText(link)
      dispatch(
        openToast({
          variant: ToastVariant.success,
          props: {
            text: 'Password Reset Link copied to clipboard.',
            description: ''
          }
        })
      )
    } catch (e) {
      const code = (e as any).response?.data?.code
      const description =
        code === 'unauthorized'
          ? 'You are not authorized for this action.'
          : (e as any).response?.data?.message ?? 'There was a problem while copying your password reset link.'
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed copying link.',
            description
          }
        })
      )
    } finally {
      setTimeout(() => {
        dispatch(closeToast())
      }, 5000)
    }
  }

  const handleDeleteAccount = async () => {
    dispatch(openModal({ type: ModalType.deleteAccount, props: modalProps.props }))
  }

  const checkPermission = (
    isOwnProfile: boolean,
    ownProfilePermission: Permission,
    otherProfilePermission: Permission
  ) => {
    if (isOwnProfile) {
      return !hasPermission(ownProfilePermission, authUser)
    }

    if (!user?.verified) return true

    return !hasPermission(otherProfilePermission, authUser)
  }

  const isAdminOrOwner = useMemo(() => hasAnyRole([Role.Admin, Role.Owner], authUser), [authUser])

  if (!user) {
    return <div>No User found.</div>
  }
  return (
    <Box>
      <Content userrole={user.role?.toLowerCase()}>
        <Box className="info-wrapper">
          <Box className="info-item">
            <Typography className="title">Name</Typography>
            <Box className="description-box">
              <Typography className="description">{user.name}</Typography>
              <ThemeButton
                themevariant="tertiary"
                onClick={handleChangeName}
                disabled={checkPermission(
                  user.id === authUser?.id,
                  Permission.UsersSelfProfileUpdate,
                  Permission.UsersProfileUpdate
                )}
                disableRipple
                disableFocusRipple
                disableTouchRipple
                disableElevation
              >
                Change Name
              </ThemeButton>
            </Box>
          </Box>
          <Box className="info-item" sx={{ marginTop: '0 !important' }}>
            <Typography className="title">Email</Typography>
            <Box className="description-box">
              <Typography className="description" data-private>
                {user.email}
              </Typography>
              <ThemeButton
                themevariant="tertiary"
                onClick={handleChangeEmail}
                disabled={checkPermission(
                  user.id === authUser?.id,
                  Permission.UsersSelfProfileUpdate,
                  Permission.UsersProfileUpdate
                )}
                disableRipple
                disableFocusRipple
                disableTouchRipple
                disableElevation
              >
                Change Email
              </ThemeButton>
            </Box>
          </Box>
          <Box className="info-item" sx={{ marginTop: '0 !important' }}>
            <Typography className="title">Password</Typography>
            <ThemeButton
              themevariant="secondary"
              onClick={handleEmailPasswordResetLink}
              disabled={checkPermission(
                user.id === authUser?.id,
                Permission.UsersSelfPasswordUpdate,
                Permission.UsersPasswordUpdate
              )}
              disableRipple
              disableFocusRipple
              disableTouchRipple
              disableElevation
            >
              <>
                <EmailIcon />
                Email Password Reset Link
              </>
            </ThemeButton>
            {!isAdminOrOwner ||
            checkPermission(
              user.id === authUser?.id,
              Permission.UsersSelfPasswordUpdate,
              Permission.UsersPasswordUpdate
            ) ? (
              <PermissionTooltip
                PopperProps={{
                  sx: {
                    marginTop: `-${theme.spacing.sm}px !important`
                  }
                }}
                title={
                  <>
                    <Box className="tooltip-wrapper-permissions">
                      <Typography className="tooltip-text">Insufficient permissions</Typography>
                    </Box>
                  </>
                }
              >
                <ThemeButton
                  themevariant="secondary"
                  sx={{ marginTop: `${theme.spacing.half}px`, ...disabledStyles.secondary }}
                  disableRipple
                  disableFocusRipple
                  disableTouchRipple
                >
                  <>
                    <CopyIcon />
                    Copy Password Reset Link
                  </>
                </ThemeButton>
              </PermissionTooltip>
            ) : (
              <ThemeButton
                themevariant="secondary"
                onClick={handleCopyPasswordResetLink}
                sx={{ marginTop: `${theme.spacing.half}px` }}
                disableRipple
                disableFocusRipple
                disableTouchRipple
                disableElevation
              >
                <>
                  <CopyIcon />
                  Copy Password Reset Link
                </>
              </ThemeButton>
            )}
          </Box>
        </Box>
        <Box className="info-wrapper" sx={{ marginTop: `${theme.spacing.medium}px` }}>
          <Box className="info-item">
            <Typography className="title">Role</Typography>
            <Box className="description-box">
              <Box className="permissions-container">
                <Box className="permissions-title">
                  {['cpi_manager', 'CPI Manager'].includes(user.role) ? 'Manager' : user.role}
                </Box>
              </Box>
              {hasPermission(Permission.UsersRoleUpdate, authUser) && (
                <ThemeButton
                  themevariant="tertiary"
                  onClick={handleChangeRole}
                  disabled={
                    checkPermission(user.id === authUser?.id, Permission.UsersRoleUpdate, Permission.UsersRoleUpdate) ||
                    !user.role
                  }
                  disableRipple
                  disableFocusRipple
                  disableTouchRipple
                  disableElevation
                >
                  Change Role
                </ThemeButton>
              )}
            </Box>
          </Box>
        </Box>
        <Box className="info-wrapper" sx={{ marginTop: `${theme.spacing.medium}px` }}>
          <Box className="info-item">
            <Typography className="title">Status</Typography>
            <Box className="description-box">
              <Box className="status-container">
                <Box className="user-status">{user.verified ? 'Active' : 'Pending Approval'}</Box>
              </Box>
              {authUser?.id === user.id && (
                <>
                  {!user.verified ? (
                    <ThemeButton
                      themevariant="tertiary"
                      onClick={handleDeleteAccount}
                      sx={{
                        width: 'fit-content',
                        color: `${theme.baseColors.info[50]} !important`,
                        "html[data-theme='light'] &": { color: `${theme.colors.inputError} !important` },
                        marginLeft: `${theme.spacing.xSmall}px`
                      }}
                      disabled={checkPermission(
                        user.id === authUser?.id,
                        Permission.UsersSelfProfileUpdate,
                        Permission.SubscriptionDelete
                      )}
                      disableRipple
                      disableFocusRipple
                      disableTouchRipple
                      disableElevation
                    >
                      Revoke Invitation & Delete Account
                    </ThemeButton>
                  ) : (
                    <ThemeButton
                      themevariant="tertiary"
                      onClick={handleDeleteAccount}
                      sx={{
                        width: 'fit-content',
                        color: `${theme.baseColors.info[50]} !important`,
                        "html[data-theme='light'] &": { color: `${theme.colors.inputError} !important` },
                        marginLeft: `${theme.spacing.xSmall}px`
                      }}
                      disabled={checkPermission(
                        user.id === authUser?.id,
                        Permission.UsersSelfProfileUpdate,
                        Permission.SubscriptionDelete
                      )}
                      disableRipple
                      disableFocusRipple
                      disableTouchRipple
                      disableElevation
                    >
                      Delete Account
                    </ThemeButton>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Content>
      {!user.verified && (
        <Actions>
          <ThemeButton
            themevariant="primary"
            onClick={handleResendInvitationLink}
            disabled={!hasPermission(Permission.UsersInvite, authUser)}
            disableRipple
            disableFocusRipple
            disableTouchRipple
            disableElevation
          >
            <>
              <EmailIcon />
              Resend Invitation Link
            </>
          </ThemeButton>
        </Actions>
      )}
    </Box>
  )
}

export default UserProfile
